import { useState, useEffect, useCallback, ReactNode } from "react";
import { pb } from "./pocketbase";
import { Brands } from "@/types/pocketbase-types";
import { BrandContext } from "@/contexts/brand-context";
import { useAuth } from "@/contexts/auth-context";

// Local storage key for storing the selected brand
const CURRENT_BRAND_KEY = "cl_current_brand";

export function BrandProvider({ children }: { children: ReactNode }) {
  const [currentBrand, setCurrentBrand] = useState<Brands | null>(null);
  const [brands, setBrands] = useState<Brands[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { user, isAuthenticated } = useAuth();

  const getBrandFromStorage = (): string | null => {
    try {
      return localStorage.getItem(CURRENT_BRAND_KEY);
    } catch (err) {
      console.error("Error accessing localStorage:", err);
      return null;
    }
  };

  const saveBrandToStorage = (brandId: string): void => {
    try {
      localStorage.setItem(CURRENT_BRAND_KEY, brandId);
    } catch (err) {
      console.error("Error saving to localStorage:", err);
    }
  };

  // Function to find a brand by ID in the brands array
  const findBrandById = (
    brandId: string,
    brandsArray: Brands[],
  ): Brands | undefined => {
    return brandsArray.find((brand) => brand.id === brandId);
  };

  const refreshBrands = useCallback(async (): Promise<void> => {
    try {
      console.log("Fetching brands...");
      setIsLoading(true);
      setError(null);

      // Fetch brands from the PocketBase 'brands' collection
      const records = await pb.collection("brands").getFullList<Brands>({
        filter: `org = "${user?.org}"`,
        sort: "-created",
      });

      console.log("Brands count - ", records.length);

      // Check if there's a stored brand ID in localStorage
      const storedBrandId = getBrandFromStorage();
      let brandToSet: Brands | null = null;

      if (storedBrandId) {
        // Try to find the stored brand in the fetched records
        const storedBrand = findBrandById(storedBrandId, records);
        if (storedBrand) {
          brandToSet = storedBrand;
        }
      }

      // If no stored brand or stored brand not found in records, use the first brand
      if (!brandToSet && records.length > 0) {
        brandToSet = records[0];
      }

      console.log("Setting brand:", brandToSet?.id);
      // Set the current brand
      if (brandToSet) {
        setCurrentBrand(brandToSet);
        saveBrandToStorage(brandToSet.id);
      }

      setBrands(records);
    } catch (err) {
      console.error("Error fetching brands:", err);
      setError(
        err instanceof Error ? err : new Error("Failed to fetch brands"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch brands only when needed (first load or auth changes)
  useEffect(() => {
    if (isAuthenticated && user?.org) {
      refreshBrands();
    } else {
      // Reset state when not authenticated
      setBrands([]);
      setCurrentBrand(null);
      setIsLoading(true);
    }
  }, [isAuthenticated, user?.org, refreshBrands]);

  // Initialize brand from localStorage on first load
  useEffect(() => {
    const initializeBrandFromStorage = () => {
      if (!isAuthenticated || !user?.org || brands?.length === 0) return;

      const storedBrandId = getBrandFromStorage();
      if (storedBrandId && !currentBrand && brands !== null) {
        const storedBrand = findBrandById(storedBrandId, brands);
        if (storedBrand) {
          console.log("Initializing brand from storage:", storedBrand.id);
          setCurrentBrand(storedBrand);
        }
      }
    };

    initializeBrandFromStorage();
  }, [isAuthenticated, user?.org, brands, currentBrand]);

  // Update localStorage when current brand changes
  useEffect(() => {
    if (currentBrand) {
      saveBrandToStorage(currentBrand.id);
    }
  }, [currentBrand]);

  // Enhanced setCurrentBrand that also updates the brands array
  const setCurrentBrandWithSync = (brand: Brands | null) => {
    setCurrentBrand(brand);

    // If setting a new brand, also update it in the brands array
    if (brand) {
      setBrands((prevBrands) => {
        const existingIndex = prevBrands.findIndex((b) => b.id === brand.id);
        if (existingIndex >= 0) {
          // Update existing brand
          const updatedBrands = [...prevBrands];
          updatedBrands[existingIndex] = brand;
          return updatedBrands;
        } else {
          // Add new brand
          return [...prevBrands, brand];
        }
      });
    }
  };

  return (
    <BrandContext.Provider
      value={{
        currentBrand,
        brands,
        isLoading,
        error,
        setCurrentBrand: setCurrentBrandWithSync,
        setBrands,
        refreshBrands,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}
